import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { Creator } from '@/types';
import { APPEAL_MIN_CHARS } from './use-appeal-form';
import {
  commonNetworkMessage,
  specificPbMessage,
  type PbErrShape,
} from '../utils/pb-error';

type AppealVars = { id: string; description: string };
type UserDeleteVars = { id: string; reason?: string };

type AppealError = { field?: 'appeal_description'; message: string };

function isAppealLengthError(e: PbErrShape): boolean {
  const raw = e.response?.message ?? '';
  const fieldMsg = e.response?.data?.['appeal_description']?.message;
  return !!fieldMsg || /appeal_description|characters/i.test(raw);
}

function parseAppealError(err: unknown): AppealError {
  const e = (err ?? {}) as PbErrShape;
  const net = commonNetworkMessage(e);
  if (net) return { message: net };
  if (e.status === 403) return { message: 'No puedes apelar este lead' };
  if (isAppealLengthError(e)) {
    const message = `Mínimo ${APPEAL_MIN_CHARS} caracteres`;
    return { field: 'appeal_description', message };
  }
  return { message: specificPbMessage(e) ?? 'Error al enviar la apelación' };
}

function parseUserDeleteError(err: unknown): string {
  const e = (err ?? {}) as PbErrShape;
  const net = commonNetworkMessage(e);
  if (net) return net;
  if (e.status === 403) return 'No puedes eliminar este lead';
  return specificPbMessage(e) ?? 'Error al eliminar el lead';
}

type AppealHookReturn = {
  mutate: (vars: AppealVars, opts?: { onSuccess?: () => void }) => void;
  isPending: boolean;
  descriptionError: string | undefined;
};

// Length errors → inline via descriptionError; everything else → toast.
export function useAppealLead(): AppealHookReturn {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [descriptionError, setDescriptionError] = useState<string>();
  const m = useMutation({
    mutationFn: (vars: AppealVars) =>
      pb.collection('leads').update<Creator>(vars.id, {
        appeal_status: 'pending_review',
        appeal_description: vars.description,
        appeal_created_at: new Date().toISOString().replace('T', ' '),
      }),
    onMutate: () => setDescriptionError(undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creators'] });
      qc.invalidateQueries({ queryKey: ['creator'] });
      toast('Apelación enviada — pendiente de revisión', 'success');
    },
    onError: (err) => {
      const p = parseAppealError(err);
      if (p.field) setDescriptionError(p.message);
      else toast(p.message, 'error');
    },
  });
  return { mutate: m.mutate, isPending: m.isPending, descriptionError };
}

export function useUserDeleteLead() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (vars: UserDeleteVars) =>
      pb.collection('leads').update<Creator>(vars.id, {
        user_deleted_at: new Date().toISOString().replace('T', ' '),
        user_deleted_reason: vars.reason ?? '',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creators'] });
      qc.invalidateQueries({ queryKey: ['creator'] });
      toast('Lead eliminado', 'success');
    },
    onError: (err) => toast(parseUserDeleteError(err), 'error'),
  });
}
